import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, Tooltip } from "recharts";
import { STATUS, ago } from "@/lib/traffic";
import { StatusPill } from "@/components/traffic/StripMap";
import { LivePing, BacklogBadge } from "@/components/traffic/Widgets";
import { useLive } from "@/hooks/useLive";
import { cn } from "@/lib/utils";

// 30-minute speed trend (2-minute buckets). Line colour follows the direction status; dashed line = "smooth" threshold.
function SpeedSpark({ data, color, smoothMin, testId }) {
  const pts = (data || []).map((p) => ({ ...p, spd: p.spd ?? null }));
  const hasData = pts.some((p) => p.spd != null);
  return (
    <div className="relative h-7 w-full" data-testid={testId}>
      {!hasData && <div className="absolute inset-0 flex items-center justify-center text-[10px] text-muted-foreground">no passages in the last 30 min</div>}
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={pts} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={`g-${color.replace("#", "")}`} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor={color} stopOpacity={0.35} />
              <stop offset="1" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          {smoothMin && hasData && <ReferenceLine y={smoothMin} stroke="currentColor" strokeOpacity={0.25} strokeDasharray="3 3" />}
          <Tooltip cursor={false} content={({ payload }) => payload?.[0] ? <div className="rounded-lg border bg-popover px-2 py-1 text-[11px] shadow">{payload[0].payload.t.slice(11, 16)} · {payload[0].payload.n} veh · {payload[0].payload.spd ?? "—"} km/h</div> : null} />
          <Area type="monotone" dataKey="spd" connectNulls stroke={color} strokeWidth={1.5} fill={`url(#g-${color.replace("#", "")})`} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function DirRow({ d, data, now, spark, smoothMin, nodet }) {
  const s = STATUS[data?.status] || STATUS.nodata;
  // 30-min trend: individual passages when the radar delivers them, otherwise the radar's own 5-minute cycle speeds
  const passages = (spark || []).filter((p) => p.spd != null).length >= 2;
  const trend = passages ? spark : (data?.spark || []).slice(-6).map((p) => ({ t: p.t, n: p.n, spd: p.n ? p.spd : null }));
  const trendVeh = (trend || []).reduce((a, p) => a + (p.n || 0), 0);
  return (
    <div className="rounded-xl bg-secondary/50 px-2 py-1" data-testid={`live-dir-${d}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold">{d === "NB" ? "Northbound" : "Southbound"}</span>
        <StatusPill status={data?.status} />
      </div>
      <div className="mt-0.5 flex items-end justify-between gap-2">
        <span className="num text-xl font-semibold leading-tight tracking-tight">{data?.n ? data.spd : "—"}<span className="ml-1 text-xs font-normal text-muted-foreground">km/h</span></span>
        <div className="num text-right text-[10px] leading-tight text-muted-foreground">
          <div>{data?.n ?? 0} veh / 5 min · occ {data?.occ ?? 0}%</div>
          <div className="flex items-center justify-end gap-1" data-testid={`live-now-${d}`}>
            <span className={cn("h-1.5 w-1.5 rounded-full", now?.n ? "bg-emerald-500 animate-pulse" : "bg-slate-300")} />
            <span>last 2 min:</span>
            <span className="font-medium text-foreground">{now?.n ? `${now.n} veh · ${now.spd} km/h` : "no passages"}</span>
          </div>
        </div>
      </div>
      <div className="mt-0.5 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>30-min trend{!passages && trendVeh ? " · 5-min cycles" : ""} · <span className="num">{trendVeh} veh</span></span>
        <span className="num">{["FL", "SL", "EL"].map((lt) => `${lt} ${data?.lanes?.[lt]?.n ?? 0}${data?.lanes?.[lt]?.n ? `·${data.lanes[lt].spd}` : ""}`).join("  ")}</span>
      </div>
      {nodet && !trendVeh ? (
        <div className="flex h-7 items-center justify-center rounded-lg border border-dashed border-orange-300/70 text-[10px] text-orange-700 dark:text-orange-300" data-testid={`live-spark-${d}`}>no vehicles detected — cycles arrive empty</div>
      ) : (
        <SpeedSpark data={trend} color={s.hex} smoothMin={smoothMin} testId={`live-spark-${d}`} />
      )}
    </div>
  );
}

export function SiteCard({ s, perMin, now, spark, smoothMin, highlight }) {
  const ref = useRef(null);
  useEffect(() => { if (highlight && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "center" }); }, [highlight]);
  return (
    <div ref={ref} data-testid={`site-card-${s.site_id}`}
      className={cn("surface p-2 transition-shadow", highlight && "ring-2 ring-primary/60", s.offline && "opacity-80")}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{s.name}</div>
          <div className="num text-[11px] text-muted-foreground">{s.site_id} · {s.ip}{s.km ? ` · KM ${s.km}` : ""}</div>
        </div>
        <div className="shrink-0 text-right">
          {s.offline ? <StatusPill status="offline" testId={`site-status-${s.site_id}`} /> : <LivePing online label={perMin ? `${perMin}/min` : "live"} />}
          <div className="num mt-1 max-w-[180px] text-[10px] text-muted-foreground" data-testid={`site-freshness-${s.site_id}`}>
            {s.offline ? s.offline_reason : `cycle ${s.last_ts?.slice(11, 16) || "—"} · ${ago(s.data_age_s)}${s.ping_rtt_ms != null ? ` · ping ${s.ping_rtt_ms} ms` : ""}`}
          </div>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-1 gap-2">
        {["NB", "SB"].filter((d) => !s.side || s.side === d).map((d) => (
          <DirRow key={d} d={d} data={s.dirs?.[d]} now={now?.[d]} spark={spark?.[d]} smoothMin={smoothMin} nodet={s.no_detections} />
        ))}
      </div>
    </div>
  );
}

export default function LiveView() {
  const { live, feed, spark } = useLive();
  const [params] = useSearchParams();
  const focus = params.get("site");
  const smoothMin = live?.thresholds?.smooth_min_kmh;
  // 20 cards in a 5 × 4 grid on a 16:9 wall display; narrower screens wrap to 4, 2 or 1 columns
  return (
    <div className="space-y-3" data-testid="live-page">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Live traffic</h1>
          <span className="eyebrow">20 radar sites · latest 5-minute cycle · 10-second feed</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <BacklogBadge feed={live?.feed} />
          <LivePing online={live?.feed?.online} label={live?.feed?.online ? `LIVE · updated ${live?.now?.slice(11, 19)} MYT` : "Radar server not reporting"} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5" data-testid="live-grid">
        {(live?.sites || []).map((s) => <SiteCard key={s.site_id} s={s} perMin={feed?.per_site?.[s.site_id]} now={feed?.now?.[s.site_id]} spark={spark?.sites?.[s.site_id]} smoothMin={smoothMin} highlight={focus === s.site_id} />)}
      </div>
    </div>
  );
}
